import React, { useState, useEffect } from "react";
import {
  Box, Typography, Card, CardContent, Button,
  Chip, CircularProgress, Alert, LinearProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";

const api = axios.create({ baseURL: "https://app.techtidellc.com" });
api.interceptors.request.use(config => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = "Bearer " + token;
  return config;
});

const impactColor = (score) => {
  if (score >= 8) return "#ef4444";
  if (score >= 7) return "#f59e0b";
  return "#6366f1";
};

export default function SEO() {
  const [health, setHealth] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [healthRes, sugRes] = await Promise.all([
        api.get("/seo/health"),
        api.get("/seo/suggestions")
      ]);
      setHealth(healthRes.data);
      setSuggestions(Array.isArray(sugRes.data) ? sugRes.data : []);
    } catch {
      setError("Failed to load SEO data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRunAudit = async () => {
    setRunning(true);
    setResult(null);
    setError("");
    try {
      const res = await api.post("/seo/run-audit");
      setResult(res.data);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Audit failed");
    } finally {
      setRunning(false);
    }
  };

  const handleDecide = async (id, decision) => {
    try {
      await api.post("/seo/suggestions/" + id + "/decide", { decision });
      load();
    } catch {
      setError("Failed to submit decision");
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress sx={{ color: "#6366f1" }} />
    </Box>
  );

  const lastRun = health?.last_run;
  const healthScore = lastRun?.health_score || 0;
  const scoreColor = healthScore >= 80 ? "#10b981" : healthScore >= 60 ? "#f59e0b" : "#ef4444";

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} sx={{ color: "#fff !important", letterSpacing: "-0.8px" }}>
          SEO Intelligence
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.35)", mt: 0.5, fontSize: 15 }}>
          On-demand SEO audit and optimization
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{
          mb: 3, borderRadius: 2,
          background: "rgba(239,68,68,0.1)",
          border: "1px solid rgba(239,68,68,0.2)",
          color: "#fca5a5"
        }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mb: 3 }}>
        <Card sx={{
          background: "rgba(13,20,36,0.8)",
          border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: "14px"
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography color="rgba(255,255,255,0.4)" fontSize={13} mb={1}>
              SEO Health Score
            </Typography>
            <Typography variant="h3" fontWeight={700} sx={{ color: scoreColor }}>
              {healthScore}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={healthScore}
              sx={{
                mt: 1, borderRadius: 4, height: 6,
                background: "rgba(255,255,255,0.1)",
                "& .MuiLinearProgress-bar": { background: scoreColor, borderRadius: 4 }
              }}
            />
          </CardContent>
        </Card>

        <Card sx={{
          background: "rgba(13,20,36,0.8)",
          border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: "14px"
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography color="rgba(255,255,255,0.4)" fontSize={13} mb={1}>
              Pending Suggestions
            </Typography>
            <Typography variant="h3" fontWeight={700} color="#fff">
              {health?.pending_suggestions || 0}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          background: "rgba(13,20,36,0.8)",
          border: "1px solid rgba(16,185,129,0.2)",
          borderRadius: "14px"
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography color="rgba(255,255,255,0.4)" fontSize={13} mb={1}>
              Approved and Sent to Dev
            </Typography>
            <Typography variant="h3" fontWeight={700} color="#10b981">
              {health?.approved_suggestions || 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Card sx={{
        background: "rgba(13,20,36,0.8)",
        border: "1px solid rgba(99,102,241,0.2)",
        borderRadius: "14px", mb: 3,
        position: "relative", overflow: "hidden"
      }}>
        <Box sx={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, #6366f1, #8b5cf6, transparent)"
        }} />
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{
                width: 40, height: 40, borderRadius: "10px",
                background: "rgba(99,102,241,0.15)",
                border: "1px solid rgba(99,102,241,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <SearchIcon sx={{ fontSize: 20, color: "#6366f1" }} />
              </Box>
              <Box>
                <Typography color="#fff" fontWeight={600} fontSize={15}>
                  Run SEO Audit
                </Typography>
                <Typography color="rgba(255,255,255,0.35)" fontSize={13}>
                  {lastRun
                    ? "Last run: " + new Date(lastRun.ran_at).toLocaleString()
                    : "Never run — click to start"}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleRunAudit}
              disabled={running}
              startIcon={running
                ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                : <SearchIcon />
              }
              sx={{
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                borderRadius: "10px", fontWeight: 600, px: 3,
                boxShadow: "0 4px 15px rgba(99,102,241,0.35)"
              }}
            >
              {running ? "Auditing..." : "Run Audit"}
            </Button>
          </Box>

          {result && (
            <Box sx={{
              mt: 3, p: 2.5, borderRadius: "10px",
              background: "rgba(0,0,0,0.3)",
              border: "1px solid rgba(255,255,255,0.06)"
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircleIcon sx={{ color: "#10b981", fontSize: 18 }} />
                <Typography color="#10b981" fontWeight={600} fontSize={14}>
                  Audit complete — {result.products_audited} products scanned
                </Typography>
              </Box>
              <Typography color="rgba(255,255,255,0.5)" fontSize={13}>
                Health Score: {result.health_score} | Suggestions: {result.suggestions_created}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography color="#fff" fontWeight={600} fontSize={15} mb={2}>
        Pending SEO Suggestions
      </Typography>

      {suggestions.length === 0 && (
        <Card sx={{
          background: "rgba(13,20,36,0.8)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "14px"
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography color="rgba(255,255,255,0.4)">
              No pending SEO suggestions — run an audit to generate recommendations
            </Typography>
          </CardContent>
        </Card>
      )}

      {suggestions.map((s) => {
        const suggestion = JSON.parse(s.suggestion_json || "{}");
        const ic = impactColor(s.impact_score);
        return (
          <Card key={s.id} sx={{
            background: "rgba(13,20,36,0.8)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: "14px", mb: 2
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box display="flex" gap={1}>
                  <Chip
                    label={"Impact: " + s.impact_score}
                    size="small"
                    sx={{
                      background: "rgba(99,102,241,0.15)",
                      color: ic,
                      fontWeight: 700, fontSize: 11
                    }}
                  />
                  <Chip
                    label={s.type?.replace("_", " ").toUpperCase()}
                    size="small"
                    sx={{
                      background: "rgba(99,102,241,0.15)",
                      color: "#a5b4fc", fontSize: 11
                    }}
                  />
                </Box>
                <Typography color="rgba(255,255,255,0.3)" fontSize={12}>
                  Confidence: {s.confidence_score}%
                </Typography>
              </Box>

              <Typography color="#fff" fontWeight={600} fontSize={14} mb={0.5}>
                {s.page_url}
              </Typography>
              <Typography color="rgba(255,255,255,0.5)" fontSize={13} mb={2}>
                {s.problem}
              </Typography>

              {suggestion.suggested_meta_title && (
                <Box sx={{
                  background: "rgba(0,0,0,0.3)", p: 2,
                  borderRadius: "8px", mb: 2
                }}>
                  <Typography color="rgba(255,255,255,0.4)" fontSize={11} mb={0.5}>
                    SUGGESTED META TITLE
                  </Typography>
                  <Typography color="#a5b4fc" fontSize={13}>
                    {suggestion.suggested_meta_title}
                  </Typography>
                  {suggestion.suggested_meta_description && (
                    <>
                      <Typography color="rgba(255,255,255,0.4)" fontSize={11} mt={1} mb={0.5}>
                        SUGGESTED META DESCRIPTION
                      </Typography>
                      <Typography color="#a5b4fc" fontSize={13}>
                        {suggestion.suggested_meta_description}
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              <Box display="flex" gap={1}>
                <Button
                  variant="contained" size="small"
                  onClick={() => handleDecide(s.id, "approved")}
                  sx={{ background: "#16a34a", borderRadius: "8px" }}
                >
                  Approve and Send to Dev
                </Button>
                <Button
                  variant="outlined" size="small"
                  onClick={() => handleDecide(s.id, "rejected")}
                  sx={{ color: "#ef4444", borderColor: "#ef4444", borderRadius: "8px" }}
                >
                  Reject
                </Button>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
